/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { getFields } from 'src/azure/services/work-items';
import { findAllFieldTypesByWorkItemField } from '../../utils/find-all-fields-types-by-wi-field';
import { getField, getFieldType } from 'src/azure/services/get-field';
import { ConditionItemType, FieldMeetCertainCriteriaErrorsType, FieldMeetCertainCriteriaInputDataType, FieldMeetCertainCriteriaType, InputValueTypes } from '../types';
import { arrayIsEmpty, stringRequired } from '../../utils/validations';

export const initialFieldMeetCertainCriteria: FieldMeetCertainCriteriaType = {
	relationship: 'this-work-item',
	relationshipCheck: 'all',
	workItemField: '',
	operator: '',
	inputValueType: 'undefined',
	selectValue: [],
	textValue: '',
	dateTimeValue: {
		type: 'datetime',
		datetime: {
			value: new Date(),
		},
		timeframe: {
			timeNumber: 0,
			timeUnit: 'minutes',
			timeOption: 'ago',
		},
	},
	userFieldValue: [''],
	iterationOrAreaPathValue: '',
};

export const timeOptions = [
	{ label: 'Ago', value: 'ago' },
	{ label: 'From now', value: 'from-now' },
];

export const timeframeOptions = [
	{ label: 'Minutes', value: 'minutes' },
	{ label: 'Hours', value: 'hours' },
	{ label: 'Days', value: 'days' },
	{ label: 'Weeks', value: 'weeks' },
	{ label: 'Months', value: 'months' },
	{ label: 'Years', value: 'years' },
];

export const supportedOperations = [
	'SupportedOperations.Equals',
	'SupportedOperations.NotEquals',
	'SupportedOperations.Contains',
	'DateCustomOperator.Before',
	'DateCustomOperator.After',
	'CustomOperator.IsEmpty',
	'CustomOperator.IsNotEmpty'
];

export function useFieldMeetCertainCriteria() {
	const getFieldOptions = useCallback(async (args: {
    projectId: string;
  }) => {
		const fields = await getFields(args.projectId);
		if (fields) {
			const foundFields = fields.filter(field => field.referenceName !== 'System.TeamProject');
			return findAllFieldTypesByWorkItemField(foundFields)
				.map(item => ({ label: item.name, value: item.referenceName }));
		}

		return [];
	}, []);

	const getFieldValueType = useCallback(async (fieldName: string): Promise<InputValueTypes> => {
		const field = await getField(fieldName);
		const fieldTypes: Array<{variants: Array<string>; result: 'text' | 'number' | 'datetime' }> = [
			{
				variants: ['Integer', 'Double'],
				result: 'number',
			},
			{
				variants: ['PlainText', 'String', 'Guid', 'Html'],
				result: 'text'
			},
			{
				variants: ['DateTime'],
				result: 'datetime'
			},
		];

		if (field.isIdentity) {
			return 'user-field';
		}

		if (['System.AreaPath','System.IterationPath'].includes(fieldName)) {
			return 'iteration-or-area-path';
		}

		if (
			[
				field.isPicklist,
				getFieldType(field.type) === 'Boolean',
				fieldName === 'Microsoft.VSTS.Common.Activity',
				fieldName === 'Microsoft.VSTS.Common.Priority',
				fieldName === 'System.WorkItemType',
				fieldName === 'System.State',
				fieldName === 'Microsoft.VSTS.CMMI.Escalate',
				fieldName === 'Microsoft.VSTS.CMMI.RequirementType',
				fieldName === 'Microsoft.VSTS.CMMI.Committed',
				fieldName === 'Microsoft.VSTS.CMMI.UserAcceptanceTest',
				fieldName === 'Microsoft.VSTS.CMMI.MeetingType',
				fieldName === 'Microsoft.VSTS.CMMI.TaskType',
				fieldName === 'Microsoft.VSTS.CMMI.RequiresReview',
				fieldName === 'Microsoft.VSTS.CMMI.RequiresTest',
				fieldName === 'Microsoft.VSTS.CMMI.RootCause',
				fieldName === 'Microsoft.VSTS.CMMI.Blocked',
				fieldName === 'Microsoft.VSTS.Common.Severity',
				fieldName === 'Microsoft.VSTS.Common.ValueArea',
				fieldName === 'Microsoft.VSTS.Common.Risk',
				fieldName === 'Microsoft.VSTS.Common.ResolvedReason',
				fieldName === 'Microsoft.VSTS.Common.Triage',
				fieldName === 'Microsoft.VSTS.Common.Discipline',
				fieldName === 'Microsoft.VSTS.Build.IntegrationBuild',
				fieldName === 'Microsoft.VSTS.Build.FoundIn',
				fieldName === 'System.BoardColumn',
				fieldName === 'System.Reason'
			].some((isTrue) => isTrue)
		) {
			return 'select';
		}

		if (field.referenceName === 'System.Tags') {
			return 'multi-select';
		}

		const foundFieldType = fieldTypes.find((itemField) => itemField.variants.includes(getFieldType(field.type)));
		if (!field.isPicklist && foundFieldType) {
			return foundFieldType.result;
		}
	
		return 'undefined';
	}, []);

	const validate = useCallback((
		inputValueType: InputValueTypes,
		values: FieldMeetCertainCriteriaType
	): {
		hasErrors: () => boolean;
		errors: FieldMeetCertainCriteriaErrorsType;
	} => {
		let hasErrors = false;
		const validations = {
			workItemField: [
				stringRequired(values.workItemField, 'This field is required'),
			],
			operator: [
				stringRequired(values.operator, 'This field is required'),
			],
			selectValue: [
				arrayIsEmpty(values.selectValue, 'This field is required'),
			],
			userField: [
				arrayIsEmpty(values.userFieldValue, 'This field is required')
			],
			textValue: [
				stringRequired(values.textValue, 'This field is required'),
			],
			iterationOrAreaPathValue: [
				Array.isArray(values.iterationOrAreaPathValue)
					? arrayIsEmpty(values.iterationOrAreaPathValue, 'This field is required')
					: stringRequired(values.iterationOrAreaPathValue, 'This field is required'),
			],
		};

		const getErrorByInputName = (name: 'workItemField' | 'operator') => {
			let resultError: string | null = null;
			const validation = new Map<string, Array<string | undefined>>()
				.set('workItemField', validations.workItemField)
				.set('operator', validations.operator)
				.get(name);

			if (validation) {
				const errors = validation.find(item => item !== undefined);
				if (errors) {
					hasErrors = true;
				}
				resultError = errors || null;
			}

			return resultError;
		};

		const getErrorByInputValueType = (inputValueTypeName: InputValueTypes) => {
			let resultError: string | null = null;
			const validation = new Map<string, Array<string | undefined>>()
				.set('select', validations.selectValue)
				.set('multi-select', validations.selectValue)
				.set('number', validations.textValue)
				.set('text', validations.textValue)
				.set('iteration-or-area-path', validations.iterationOrAreaPathValue)
				.set('user-field', validations.userField)
				.get(inputValueTypeName);

			if (validation && (values.operator !== 'CustomOperator.IsEmpty' && values.operator !== 'CustomOperator.IsNotEmpty')) {
				const errors = validation.find(item => item !== undefined);
				if (errors) {
					hasErrors = true;
				}
				resultError = errors || null;
			}

			return resultError;
		};

		return {
			hasErrors: () => hasErrors,
			errors: {
				workItemField: getErrorByInputName('workItemField'),
				operator: getErrorByInputName('operator'),
				selectValue: getErrorByInputValueType(inputValueType),
				dateTimeValue: getErrorByInputValueType(inputValueType),
				textValue: getErrorByInputValueType(inputValueType),
				userFieldValue: getErrorByInputValueType(inputValueType),
				iterationOrAreaPathValue: getErrorByInputValueType(inputValueType),
			}
		};
	}, []);

	const getValues = useCallback((data: FieldMeetCertainCriteriaType) => {
		const isEmptyOperator = data.operator === 'CustomOperator.IsEmpty' || data.operator === 'CustomOperator.IsNotEmpty';
		const isInOrNotInOperator = data.operator === 'SupportedOperations.In' || data.operator === 'SupportedOperations.NotIn';

		const hasRelationshipCheck = data.relationship === 'sibling-work-items' || data.relationship === 'children-work-items';
		const relationshipCheck = hasRelationshipCheck ? data.relationshipCheck : null;

		if (data.inputValueType === 'multi-select') {
			return {
				type: 'field-meet-a-certain-criteria-with-multiple-values',
				relationship: data.relationship,
				relationshipCheck: relationshipCheck,
				data: {
					field: data.workItemField,
					operator: data.operator,
					value:  data.selectValue
				}
			};
		}

		if (data.inputValueType === 'user-field') {
			const handleValue = () => {
				if (isInOrNotInOperator) {
					return {
						type: 'field-meet-a-certain-criteria-with-multiple-values',
						relationship: data.relationship,
						relationshipCheck: relationshipCheck,
						data: {
							field: data.workItemField,
							operator: data.operator,
							value: data.userFieldValue,
						}
					};
				}

				if (isEmptyOperator) {
					return {
						type: 'field-meet-a-certain-criteria',
						relationship: data.relationship,
						relationshipCheck: relationshipCheck,
						data: {
							field: data.workItemField,
							operator: data.operator,
							value: '',
						}
					};
				}

				return {
					type: 'field-meet-a-certain-criteria',
					relationship: data.relationship,
					relationshipCheck: relationshipCheck,
					data: {
						field: data.workItemField,
						operator: data.operator,
						value: data.userFieldValue[0],
					}
				};
			};

			return handleValue();
		}

		if (data.inputValueType === 'select') {
			const handleValue = () => {
				if (isInOrNotInOperator) {
					return {
						type: 'field-meet-a-certain-criteria-with-multiple-values',
						relationship: data.relationship,
						relationshipCheck: relationshipCheck,
						data: {
							field: data.workItemField,
							operator: data.operator,
							value: data.selectValue,
						}
					};
				}

				if (isEmptyOperator) {
					return {
						type: 'field-meet-a-certain-criteria',
						relationship: data.relationship,
						relationshipCheck: relationshipCheck,
						data: {
							field: data.workItemField,
							operator: data.operator,
							value: '',
						}
					};
				}

				return {
					type: 'field-meet-a-certain-criteria',
					relationship: data.relationship,
					relationshipCheck: relationshipCheck,
					data: {
						field: data.workItemField,
						operator: data.operator,
						value: data.selectValue[0],
					}
				};
			};

			return handleValue();
		}
	
		if (data.inputValueType === 'datetime') {
			return {
				type: 'field-meet-a-certain-criteria',
				relationship: data.relationship,
				relationshipCheck: relationshipCheck,
				data: {
					field: data.workItemField,
					operator: data.operator,
					value: isEmptyOperator ? '' : data.dateTimeValue
				}
			};
		}

		if (data.inputValueType === 'iteration-or-area-path') {
			let result = {
				type: 'field-meet-a-certain-criteria',
				relationship: data.relationship,
				relationshipCheck: relationshipCheck,
				data: {
					field: data.workItemField,
					operator: data.operator,
					value: isEmptyOperator ? '' : data.iterationOrAreaPathValue
				}
			};

			if (isInOrNotInOperator) {
				result = {
					...result,
					type: 'field-meet-a-certain-criteria-with-multiple-values',
				};
			}

			return result;
		}
			
		return {
			type: 'field-meet-a-certain-criteria',
			relationship: data.relationship,
			relationshipCheck: relationshipCheck,
			data: {
				field: data.workItemField,
				operator: data.operator,
				value: isEmptyOperator ? '' : data.textValue
			}
		};
	}, []);

	const loadValues = useCallback(async (values: FieldMeetCertainCriteriaInputDataType) => {
		const data: FieldMeetCertainCriteriaType = {
			relationship: values.relationship,
			relationshipCheck: 'all',
			workItemField: '',
			operator: '',
			inputValueType: 'undefined',
			selectValue: [],
			textValue: '',
			dateTimeValue: {
				type: 'datetime',
				datetime: {
					value: new Date(),
				},
				timeframe: {
					timeNumber: 0,
					timeUnit: 'minutes',
					timeOption: 'ago',
				},
			},
			userFieldValue: [''],
			iterationOrAreaPathValue: '',
		};

		const fieldType = await getFieldValueType(values.field);

		data.workItemField = values.field;
		data.operator = values.operator;
		data.inputValueType = fieldType;

		if (fieldType === 'select'
			&& values.operator !== 'SupportedOperations.In'
			&& values.operator !== 'SupportedOperations.NotIn'
		) {
			data.selectValue = [values.value];
		}

		if (fieldType === 'select'
			&& (values.operator === 'SupportedOperations.In' || values.operator === 'SupportedOperations.NotIn')
		) {
			data.selectValue = values.value;
		}

		if (fieldType === 'multi-select') {
			data.selectValue = values.value;
		}
	

		if (fieldType === 'number' || fieldType === 'text') {
			data.textValue = values.value;
		}

		if (fieldType === 'datetime') {
			if (values.value.type === 'datetime') {
				data.dateTimeValue = {
					type: 'datetime',
					datetime: {
						value: new Date(values.value.datetime.value),
					},
					timeframe: {
						timeNumber: 0,
						timeUnit: 'minutes',
						timeOption: 'ago',
					},
				};
			}

			if (values.value.type === 'timeframe') {
				data.dateTimeValue = {
					type: 'timeframe',
					datetime: { 
						value: new Date(),
					},
					timeframe: {
						timeNumber: values.value.timeframe.timeNumber,
						timeUnit: values.value.timeframe.timeUnit,
						timeOption: values.value.timeframe.timeOption,
					},
				};
			}
		}

		if (fieldType === 'user-field' && !Array.isArray(values.value)) {
			data.userFieldValue = [values.value];
		}

		if (fieldType === 'user-field' && Array.isArray(values.value)) {
			data.userFieldValue = values.value;
		}

		if (fieldType === 'iteration-or-area-path') {
			data.iterationOrAreaPathValue = values.value;
		}

		return data;
	}, []);

	const setCondition = useCallback((input: {
		conditionId: string;
		setConditions: React.Dispatch<React.SetStateAction<Array<ConditionItemType>>>;
		condition: string;
	}) => {
		input.setConditions(oldConditions => {
			const others = oldConditions.filter(item => item.id !== input.conditionId) as ConditionItemType[];
			const current = oldConditions.find(item => item.id === input.conditionId) as ConditionItemType;

			return [
				...others, {
					...current,
					condition: input.condition,
					checklistCompletedOrNotCompletedCondition: null,
					fieldMeetCertainCriteria: initialFieldMeetCertainCriteria,
				}
			];
		});
	}, []);

	return {
		getFieldOptions,
		getFieldValueType,
		validate,
		getValues,
		loadValues,
		setCondition
	};
}